// ProjectMetaData.js
import { useMemo } from "react";
import { Box, Grid, Stack, Divider, Chip, Tooltip } from "@mui/material";
import BudgetTable from "./budgetTable";
import { RevenueSharingTable } from "./revenueSharingTable";
import ExpandableText from "./expandableText";
import Section from "./section";
import LabelValue from "./labelValue";
import Empty from "./empty";
import DiffInline from "./diffInline";
import {
  fmtDate,
  fmtDateTime,
  fmtDateTimeLocalFromUTC,
  statuses,
} from "../helpers";
const moment = require("moment");

const ProjectMetaData = ({
  project,
  unit,
  units,
  activity,
  activities,
  status,
  changes,
  selectedChange,
  ChangeText,
  ChangeTextChip,
  canSeeBudget,
}) => {
  const budgetRows = useMemo(() => {
    try {
      if (selectedChange) {
        const parsedOld = JSON.parse(changes.budget.old || "[]");
        const parsedNew = JSON.parse(changes.budget.new || "[]");
        const parsed = [];
        for (const row of parsedNew) {
          parsed.push({
            name: { new: row.name, old: null },
            value: { new: row.value, old: null },
          });
        }
        for (const row of parsedOld) {
          const match = parsed.find((r) => r.name.new === row.name);
          if (match) {
            match.name.old = row.name;
            match.value.old = row.value;
          } else {
            parsed.push({
              name: { new: null, old: row.name },
              value: { new: null, old: row.value },
            });
          }
        }
        return Array.isArray(parsed) ? parsed : [];
      }
      return project.budgetRows || [];
    } catch {
      return [];
    }
  }, [project?.budgetRows, changes, selectedChange]);

  const revenueRows = useMemo(() => {
    try {
      if (selectedChange) {
        const parsedOld = JSON.parse(changes.revenue_sharing.old || "[]");
        const parsedNew = JSON.parse(changes.revenue_sharing.new || "[]");
        const parsed = [];
        for (const row of parsedNew) {
          parsed.push({
            unit: { new: row.unit, old: null },
            percent: { new: row.percent, old: null },
            euro: { new: row.euro, old: null },
          });
        }
        for (const row of parsedOld) {
          const match = parsed.find((r) => r.unit.new === row.unit);
          if (match) {
            match.unit.old = row.unit;
            match.percent.old = row.percent;
            match.euro.old = row.euro;
          } else {
            parsed.push({
              unit: { new: null, old: row.unit },
              percent: { new: null, old: row.percent },
              euro: { new: null, old: row.euro },
            });
          }
        }
        return Array.isArray(parsed) ? parsed : [];
      }
      const parsed = JSON.parse(project?.revenue_sharing || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [project?.revenue_sharing, changes, selectedChange]);

  const startDate = fmtDate(project?.start_date);
  const endDate = fmtDate(project?.end_date);
  const dateSubmitted = fmtDateTimeLocalFromUTC(project?.date_submitted);
  const decisionDate = fmtDateTime(project?.decision_date);
  const approvalDate = fmtDateTime(project?.approval_date);
  const confirmDate = fmtDateTime(project?.confirm_date);

  const durationChip = useMemo(() => {
    if (!project?.start_date || !project?.end_date) return null;
    const a = moment(project.start_date, "YYYY-MM-DD");
    const b = moment(project.end_date, "YYYY-MM-DD");
    const days = b.diff(a, "days") + 1;
    if (!Number.isFinite(days) || days <= 0) return null;
    return (
      <Tooltip title="Planned duration">
        <Chip
          size="small"
          variant="outlined"
          label={`${days} day${days === 1 ? "" : "s"}`}
        />
      </Tooltip>
    );
  }, [project?.start_date, project?.end_date]);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {/* Top summary tables */}
      {canSeeBudget && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6} lg={6}>
            <Section title="Budget" subtitle="Summary of planned amounts">
              <BudgetTable
                rows={budgetRows}
                ChangeText={ChangeText}
                selectedChange={selectedChange}
              />
            </Section>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <Section
              title="EBS Revenue Sharing"
              subtitle="Distribution by unit"
            >
              <RevenueSharingTable
                rows={revenueRows}
                units={units}
                ChangeText={ChangeText}
                selectedChange={selectedChange}
              />
            </Section>
          </Grid>
        </Grid>
      )}

      <Section title="Details">
        <Stack spacing={1.25} divider={<Divider flexItem />}>
          <LabelValue label="Code">
            {selectedChange ? (
              changes.code &&
              changes.status_id?.new > statuses.PREPARING_FOR_SIGNOFF ? (
                <ChangeText
                  newValue={changes.code.new ?? "Not Issued Yet"}
                  oldValue={changes.code.old ?? "Not Issued Yet"}
                ></ChangeText>
              ) : (
                <Empty children="Not Issued Yet" />
              )
            ) : status.status_id >= statuses.CONFIRMED ? (
              project?.code
            ) : (
              <Empty>Not Issued Yet</Empty>
            )}
          </LabelValue>

          <LabelValue label="Description">
            {selectedChange ? (
              changes.description ? (
                <DiffInline
                  newText={changes.description.new}
                  oldText={changes.description.old}
                ></DiffInline>
              ) : (
                <Empty />
              )
            ) : (
              <ExpandableText text={project?.description} lines={3} />
            )}
          </LabelValue>
          <LabelValue label="Unit">
            {selectedChange ? (
              changes.unit_id ? (
                <ChangeText
                  newValue={
                    units.find((u) => u.unit_id === changes.unit_id.new)
                      ?.unit_name ?? ""
                  }
                  oldValue={
                    units.find((u) => u.unit_id === changes.unit_id.old)
                      ?.unit_name ?? ""
                  }
                ></ChangeText>
              ) : (
                <Empty />
              )
            ) : (
              unit?.unit_name || <Empty />
            )}
          </LabelValue>
          <LabelValue label="Activity">
            {selectedChange ? (
              changes.activity_id ? (
                <ChangeText
                  newValue={
                    activities.find(
                      (u) => u.activity_id === changes.activity_id.new,
                    )?.activity_name ?? ""
                  }
                  oldValue={
                    activities.find(
                      (u) => u.activity_id === changes.activity_id.old,
                    )?.activity_name ?? ""
                  }
                ></ChangeText>
              ) : (
                <Empty />
              )
            ) : (
              activity?.activity_name || <Empty />
            )}
          </LabelValue>
          {selectedChange
            ? changes.subactivities
              ? Array.from({
                  length: Math.max(
                    changes.subactivities.new?.length ?? 0,
                    changes.subactivities.old?.length ?? 0,
                  ),
                }).map((_, i) => {
                  const newSub = changes.subactivities.new[i];
                  const oldSub = changes.subactivities.old[i];
                  if (
                    oldSub &&
                    newSub &&
                    oldSub?.Category?.category_name !==
                      newSub?.Category?.category_name
                  ) {
                    return (
                      <Box key={i}>
                        <LabelValue
                          key={i}
                          label={
                            newSub?.Category?.category_name ?? "Subactivity"
                          }
                        >
                          <ChangeText
                            newValue={
                              changes.subactivities.new[i]?.subactivity_name
                            }
                            oldValue={null}
                          ></ChangeText>
                        </LabelValue>
                        <LabelValue
                          textDecoration={"line-through"}
                          key={i}
                          label={
                            oldSub?.Category?.category_name ?? "Subactivity"
                          }
                        >
                          <ChangeText
                            newValue={null}
                            oldValue={
                              changes.subactivities.old[i]?.subactivity_name
                            }
                          ></ChangeText>
                        </LabelValue>
                      </Box>
                    );
                  }

                  return (
                    <LabelValue
                      key={i}
                      label={newSub?.Category?.category_name ?? "Subactivity"}
                    >
                      <ChangeText
                        newValue={
                          changes.subactivities.new[i]?.subactivity_name
                        }
                        oldValue={
                          changes.subactivities.old[i]?.subactivity_name ?? ""
                        }
                      ></ChangeText>
                    </LabelValue>
                  );
                })
              : ""
            : project?.subactivities?.map((sub, i) => {
                return (
                  <LabelValue
                    key={i}
                    label={sub.Category?.category_name ?? "Subactivity"}
                  >
                    <Box>
                      {sub.subactivity_name ? (
                        <>{sub.subactivity_name}</>
                      ) : (
                        <Empty>-</Empty>
                      )}
                    </Box>
                  </LabelValue>
                );
              })}

          <LabelValue label="Client Name">
            {selectedChange ? (
              changes.client_name ? (
                <ChangeText
                  newValue={changes.client_name.new}
                  oldValue={changes.client_name.old}
                ></ChangeText>
              ) : (
                <Empty />
              )
            ) : (
              project?.client_name || <Empty />
            )}
          </LabelValue>

          <LabelValue label="Project Manager">
            {selectedChange ? (
              changes.project_manager_name && changes.project_manager_email ? (
                <ChangeTextChip
                  newValue={changes.project_manager_name.new}
                  newChip={changes.project_manager_email.new}
                  oldValue={changes.project_manager_name.old}
                  oldChip={changes.project_manager_email.old}
                ></ChangeTextChip>
              ) : (
                <Empty />
              )
            ) : project?.project_manager_name ? (
              <>
                {project.project_manager_name}
                {project.project_manager_email && (
                  <Chip
                    size="small"
                    label={project.project_manager_email}
                    sx={{ ml: 1 }}
                    variant="outlined"
                  />
                )}
              </>
            ) : (
              <Empty />
            )}
          </LabelValue>

          <LabelValue label="Expenses Confirmed By">
            {selectedChange ? (
              changes.expenses_confirmed_by_name &&
              changes.expenses_confirmed_by_email ? (
                <ChangeTextChip
                  newValue={changes.expenses_confirmed_by_name.new}
                  newChip={changes.expenses_confirmed_by_email.new}
                  oldValue={changes.expenses_confirmed_by_name.old}
                  oldChip={changes.expenses_confirmed_by_email.old}
                ></ChangeTextChip>
              ) : (
                <Empty />
              )
            ) : project?.expenses_confirmed_by_name ? (
              <>
                {project.expenses_confirmed_by_name}
                {project.expenses_confirmed_by_email && (
                  <Chip
                    size="small"
                    label={project.expenses_confirmed_by_email}
                    sx={{ ml: 1 }}
                    variant="outlined"
                  />
                )}
              </>
            ) : (
              <Empty />
            )}
          </LabelValue>

          <LabelValue label="Start Date">
            {selectedChange ? (
              changes.start_date ? (
                <ChangeText
                  newValue={fmtDate(changes.start_date.new)}
                  oldValue={fmtDate(changes.start_date.old)}
                ></ChangeText>
              ) : (
                <Empty />
              )
            ) : (
              startDate || <Empty />
            )}
          </LabelValue>

          <LabelValue label="End Date">
            {selectedChange ? (
              changes.end_date ? (
                <ChangeTextChip
                  newValue={fmtDate(changes.end_date.new)}
                  newChip={
                    moment(changes.end_date.new, "YYYY-MM-DD").diff(
                      moment(changes.start_date.new, "YYYY-MM-DD"),
                      "days",
                    ) +
                    1 +
                    " days"
                  }
                  oldValue={fmtDate(changes.end_date.old)}
                  oldChip={
                    isNaN(
                      moment(changes.end_date.old, "YYYY-MM-DD").diff(
                        moment(changes.start_date.old, "YYYY-MM-DD"),
                        "days",
                      ) + 1,
                    )
                      ? null
                      : moment(changes.end_date.old, "YYYY-MM-DD").diff(
                          moment(changes.start_date.old, "YYYY-MM-DD"),
                          "days",
                        ) +
                        1 +
                        " days"
                  }
                ></ChangeTextChip>
              ) : (
                <Empty />
              )
            ) : (
              <>
                {endDate || <Empty />}
                {durationChip && (
                  <Box component="span" sx={{ ml: 1 }}>
                    {durationChip}
                  </Box>
                )}
              </>
            )}
          </LabelValue>

          <LabelValue label="Project Members">
            <Stack spacing={1}>
              {selectedChange ? (
                changes.members ? (
                  Array.from({
                    length: Math.max(
                      changes.members.new?.length ?? 0,
                      changes.members.old?.length ?? 0,
                    ),
                  }).map((_, i) => {
                    return (
                      <ChangeTextChip
                        key={i}
                        newValue={changes.members.new[i]?.name}
                        newChip={changes.members.new[i]?.email}
                        oldValue={changes.members.old[i]?.name}
                        oldChip={changes.members.old[i]?.email}
                      ></ChangeTextChip>
                    );
                  })
                ) : (
                  <Empty />
                )
              ) : project.members.length > 0 ? (
                project.members.map((m, index) => (
                  <Stack direction="row" key={index}>
                    {m.name ? (
                      <>
                        {m.name}
                        {m.email && (
                          <Chip
                            size="small"
                            label={m.email}
                            sx={{ ml: 1 }}
                            variant="outlined"
                          />
                        )}
                        {canSeeBudget && (
                          <Box
                            sx={{ pl: 1 }}
                          >{`${m.hours} h, ${m.hour_rate} €/h`}</Box>
                        )}
                      </>
                    ) : (
                      <Empty />
                    )}
                  </Stack>
                ))
              ) : (
                <Empty />
              )}
            </Stack>
          </LabelValue>

          <LabelValue label="Notes">
            {changes.notes ? (
              changes.notes ? (
                <ChangeText
                  newValue={changes.notes.new}
                  oldValue={changes.notes.old}
                ></ChangeText>
              ) : (
                <Empty />
              )
            ) : (
              project?.notes || <Empty />
            )}
          </LabelValue>

          <LabelValue label="Date Submitted">
            {dateSubmitted || <Empty>Not Submitted</Empty>}
          </LabelValue>

          <LabelValue label="Last Decision Date">
            {decisionDate || <Empty>No Decision Yet</Empty>}
          </LabelValue>

          <LabelValue label="Unit Manager Approval Date">
            {approvalDate || <Empty>Not Approved</Empty>}
          </LabelValue>

          <LabelValue label="Accounting Confirmation Date">
            {confirmDate || <Empty>Not Confirmed</Empty>}
          </LabelValue>
        </Stack>
      </Section>
    </Box>
  );
};

export default ProjectMetaData;
